import React, { useState } from "react";
import { Copy, Eye, EyeOff, Info } from "lucide-react";
import { Button } from "../ui/button";

const EnhancedOcrViewer = ({ text, onChange, readOnly }) => {
  const [viewMode, setViewMode] = useState("formatted"); // "formatted" or "raw"

  const lines = text ? text.split("\n") : [];

  const handleCopy = () => {
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        // Toast notification is handled by parent
      });
    }
  };

  return (
    <div className="relative h-full flex flex-col">
      <div className="absolute top-2 right-2 z-10 flex space-x-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            setViewMode(viewMode === "formatted" ? "raw" : "formatted")
          }
          title={
            viewMode === "formatted"
              ? "View Text Details"
              : "View Formatted Text"
          }
        >
          {viewMode === "formatted" ? (
            <Info className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </Button>
        {text && (
          <Button
            size="sm"
            variant="secondary"
            onClick={handleCopy}
            title="Copy to clipboard"
          >
            <Copy className="h-4 w-4" />
          </Button>
        )}
      </div>

      {viewMode === "formatted" ? (
        <div className="flex-1 overflow-auto">
          <div className="flex h-full">
            {/* Line numbers */}
            <div className="bg-gray-100 text-gray-500 text-right pr-2 py-4 select-none">
              {lines.map((_, idx) => (
                <div key={idx} className="h-6 text-xs">
                  {idx + 1}
                </div>
              ))}
              {lines.length === 0 && <div className="h-6 text-xs">1</div>}
            </div>

            {/* Text display */}
            <div className="flex-1 py-4 pr-4 font-mono text-sm">
              {readOnly ? (
                <pre className="whitespace-pre-wrap break-all">{text}</pre>
              ) : (
                <textarea
                  value={text}
                  onChange={(e) => onChange(e)}
                  className="w-full h-full border-none resize-none focus:outline-none font-mono"
                  placeholder="Extract OCR text from PDF..."
                  readOnly={readOnly}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-4">
          <div className="bg-gray-50 border rounded-md p-4">
            <h3 className="text-sm font-semibold mb-2">Position Information</h3>
            <pre className="bg-gray-800 text-green-300 p-3 rounded-md overflow-auto text-xs font-mono whitespace-pre">
              {JSON.stringify(
                JSON.parse(
                  text
                    ? `{"text":"${text
                        .replace(/\n/g, "\\n")
                        .replace(/"/g, '\\"')}"}`
                    : "{}"
                ),
                null,
                2
              )}
            </pre>
          </div>

          <div className="mt-4">
            <h3 className="text-sm font-semibold mb-2">
              Character Positions & Line Statistics
            </h3>
            <div className="bg-white border rounded-md p-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p>
                    <strong>Total Lines:</strong> {lines.length}
                  </p>
                  <p>
                    <strong>Total Characters:</strong> {text?.length || 0}
                  </p>
                </div>
                <div>
                  <p>
                    <strong>Longest Line:</strong>{" "}
                    {text ? Math.max(...lines.map((l) => l.length), 0) : 0}{" "}
                    chars
                  </p>
                  <p>
                    <strong>Average Line Length:</strong>{" "}
                    {text && lines.length
                      ? Math.round(
                          lines.reduce((sum, line) => sum + line.length, 0) /
                            lines.length
                        )
                      : 0}{" "}
                    chars
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <h4 className="font-semibold mb-1">
                  Line Length Distribution:
                </h4>
                {lines.length > 0 && (
                  <div className="space-y-1">
                    {lines.map((line, idx) => (
                      <div key={idx} className="flex items-center text-xs">
                        <span className="w-8 text-right mr-2">{idx + 1}:</span>
                        <div
                          className="bg-blue-500 h-4 rounded"
                          style={{
                            width: `${Math.min(
                              100,
                              (line.length / 80) * 100
                            )}%`,
                          }}
                        ></div>
                        <span className="ml-2">{line.length} chars</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedOcrViewer;
